import {
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { eventService } from "../../../shared/api/eventService";
import { userResponsibilityService } from "../../../shared/api/userResponsibilityService";
import { PermissionName } from "../../../shared/types/permission";
import {
	UserResponsibilityFormValues,
	UserResponsibilityResource,
	UserResponsibilitySaveRequest,
	UserResponsibilityType,
} from "../../../shared/types/userResponsibility";
import { eventTypeService } from "../shared/api/eventTypeService";
import { UserResponsibilityCreateDrawer, UserResponsibilityUpdateDrawer } from "./UserResponsibilityDrawer";
import { EvenResponsibilityListContextMenu } from "./UserResponsibilityListContextMenu";

const mapFormValuesToRequest = (values: UserResponsibilityFormValues): UserResponsibilitySaveRequest => ({
	translations: values.translations,
	type: values.type as UserResponsibilityType,
});
export const UserResponsibilityList: FC = () => {
	const { t } = useTranslation();

	const crudOperations = useLaravelCrudEntity(
		userResponsibilityService,
		{
			entityName: t("User responsibility"),
			messages: {
				create: t("User responsibility was saved."),
				update: t("User responsibility was saved."),
				delete: t("User responsibility was deleted."),
			},
		},
		[eventService.baseKey, eventTypeService.baseKey],
	);

	const dataListColumns: IDrawerDataListColumn<UserResponsibilityResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: t("Type"),
				valueKey: "type",
				type: "value",
				render: ({ type }) => t(`userResponsibilityType.${type}`),
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: (userResponsibility, openDrawer) => (
					<EvenResponsibilityListContextMenu
						userResponsibility={userResponsibility}
						handleEdit={openDrawer}
						handleDelete={() => crudOperations.deleteEntity(userResponsibility.id)}
					/>
				),
			},
		],
		[crudOperations, t],
	);
	const canCreate = useHasPermission<PermissionName>(["userResponsibility.create"]);
	const canUpdate = useHasPermission<PermissionName>(["userResponsibility.update"]);

	return (
		<PaginatedDrawerAutoDataList
			canUpdate={canUpdate}
			canCreate={canCreate}
			initialSearch={{
				search: "",
				searchFields: [{ name: "name", label: t("Name") }],
			}}
			translations={{
				title: t("User responsibilities"),
				empty: t("No user responsibilities available"),
			}}
			renderCreateDrawer={(drawerProps) => (
				<UserResponsibilityCreateDrawer
					{...drawerProps}
					onSubmit={(formValues) => crudOperations.createEntity(mapFormValuesToRequest(formValues))}
				/>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<UserResponsibilityUpdateDrawer
					{...drawerProps}
					onSubmit={(formValues) => crudOperations.updateEntity(drawerProps.id, mapFormValuesToRequest(formValues))}
				/>
			)}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(
				userResponsibilityService.listKey,
				userResponsibilityService.index,
			)}
		/>
	);
};
